import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { RAILS_API_URL } from 'config/apiUrl'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { createNestToken, refreshRailsSession } from 'redux/slices/auth-slice'
import { store } from 'redux/store'
import { getDeviceFingerprint } from 'services/device-fingerprint'

class RailsApiService {
  apiInstance: AxiosInstance

  constructor() {
    this.apiInstance = axios.create({
      baseURL: RAILS_API_URL
    })

    this.apiInstance.interceptors.request.use((config) => {
      const authState = store.getState().auth
      const shouldDecamelize = config.headers['Content-Type'] !== 'multipart/form-data'

      return {
        ...config,
        data: shouldDecamelize ? decamelizeKeys(config.data) : config.data,
        headers: {
          Authorization: config.headers.Authorization || `${authState.railsTokenType} ${authState.railsToken}`,
          'X-Device-Fingerprint': getDeviceFingerprint()
        }
      } as unknown as InternalAxiosRequestConfig
    })

    this.apiInstance.interceptors.response.use(
      (config) => ({ ...config, data: camelizeKeys(config.data) }),
      async ({ config, response }) => {
        // handle case where accessToken has been expired: refresh it!
        if (config && !config.url.includes('spree_oauth') && response.status === 401) {
          const { tokenType, accessToken } = await store.dispatch(refreshRailsSession()).unwrap()
          await store.dispatch(createNestToken({ accessToken })).unwrap()
          config.headers.Authorization = `${tokenType} ${accessToken}`
          return axios.request(config)
        }

        return Promise.reject(response)
      }
    )
  }

  async uploadMedicalDocument(formData: FormData) {
    return await this.apiInstance.patch('/api/v1/profile/medical_documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async deleteMedicalDocument({ id, awsPhoto }: { id: number; awsPhoto: boolean }) {
    return await railsApiService.apiInstance.delete(`/api/v1/profile/medical_documents/${id}`, {
      data: {
        awsPhoto
      }
    })
  }
}

const railsApiService = new RailsApiService()

export default railsApiService
